import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function JoinSection() {
  const [stats, setStats] = useState({ pledges: 0, pledge_goal: 1000000, ambassadors: 0 });
  const [pledge, setPledge] = useState({ name: "", email: "" });
  const [amb, setAmb] = useState({ name: "", email: "", city: "", why: "" });
  const [busyP, setBusyP] = useState(false);
  const [busyA, setBusyA] = useState(false);
  const [cert, setCert] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const submitPledge = async (e) => {
    e.preventDefault();
    setBusyP(true);
    try {
      const { data } = await api.post("/pledge", pledge);
      setStats((s) => ({ ...s, pledges: data.count }));
      setCert({ name: data.name, n: data.pledge_no });
      setPledge({ name: "", email: "" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyP(false);
    }
  };

  const submitAmb = async (e) => {
    e.preventDefault();
    setBusyA(true);
    try {
      await api.post("/ambassadors", amb);
      toast.success("Welcome to the Ambassador circle. Check your inbox.");
      setAmb({ name: "", email: "", city: "", why: "" });
      api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusyA(false);
    }
  };

  const inputCls = "rounded-none border-0 border-b border-[#33240E]/25 bg-transparent focus-visible:ring-0 focus-visible:border-[#8A5A0F]";
  const labelCls = "font-mono text-xs uppercase tracking-widest text-muted-foreground";

  return (
    <section id="pledge" className="mx-auto max-w-7xl px-6 py-32 lg:px-12" data-testid="join-section">
      <div className="grid gap-20 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.4em] text-[#8A5A0F]">Chapter 03</p>
          <h2 className="mb-4 font-serif text-4xl font-black text-[#33240E] sm:text-5xl">
            Take the <span className="italic text-[#8A5A0F]">pledge</span>
          </h2>
          <div className="mb-8 flex items-end gap-4">
            <span className="font-serif text-6xl font-black text-[#8A5A0F] lg:text-7xl" data-testid="pledge-counter">{stats.pledges.toLocaleString()}</span>
            <span className="pb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">of 1,000,000 pledges</span>
          </div>
          <p className="mb-8 max-w-md text-base font-light italic leading-relaxed text-muted-foreground">
            We, the members of the Be Kind Community, unite under a shared vision of love and kindness,
            expanding our hearts and minds to embrace others with compassion and understanding.
          </p>
          <ul className="mb-10 max-w-md space-y-4" data-testid="pledge-principles">
            {[
              "Recognize love as the core of kindness, understanding that our actions and words have the power to heal, uplift, and inspire.",
              "Grow continuously from within, nurturing our inner strengths and embracing our weaknesses with grace.",
              "Prioritize our mental health, acknowledging that it's perfectly okay not to be okay. We commit to seeking help when needed.",
              "Foster a safe and supportive environment for everyone to express themselves freely and without judgment.",
            ].map((line) => (
              <li key={line.slice(0, 24)} className="flex items-start gap-3 text-sm font-light leading-relaxed text-[#33240E]/80">
                <span className="mt-1 inline-block h-2 w-2 shrink-0 rotate-45 border border-[#8A5A0F]" />
                {line}
              </li>
            ))}
          </ul>
          <form onSubmit={submitPledge} className="space-y-6" data-testid="pledge-form">
            <div className="space-y-2">
              <Label htmlFor="pledge-name" className={labelCls}>Name</Label>
              <Input id="pledge-name" data-testid="pledge-name-input" value={pledge.name} onChange={(e) => setPledge({ ...pledge, name: e.target.value })} required className={inputCls} placeholder="Your full name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pledge-email" className={labelCls}>Email</Label>
              <Input id="pledge-email" type="email" data-testid="pledge-email-input" value={pledge.email} onChange={(e) => setPledge({ ...pledge, email: e.target.value })} required className={inputCls} placeholder="you@example.com" />
            </div>
            <button type="submit" disabled={busyP} data-testid="pledge-submit-button"
              className="w-full bg-[#33240E] py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#F6E7C1] transition-colors duration-300 hover:bg-[#4A3517] disabled:opacity-50">
              {busyP ? "Counting you in..." : "I Pledge to Be Kind"}
            </button>
          </form>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
          className="border-t border-[#33240E]/15 pt-12 lg:border-l lg:border-t-0 lg:pl-20 lg:pt-0">
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.4em] text-velvet">Go Deeper</p>
          <h2 className="mb-4 font-serif text-4xl font-black text-[#33240E] sm:text-5xl">
            Become an <span className="italic text-[#8A5A0F]">ambassador</span>
          </h2>
          <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A5A0F]" data-testid="ambassador-counter">
            <span className="font-serif text-3xl font-black">{stats.ambassadors.toLocaleString()}</span>&nbsp;&nbsp;ambassadors activated
          </p>
          <p className="mb-10 max-w-md text-base font-light leading-relaxed text-muted-foreground">
            Ambassadors carry the movement into their city — recruiting friends, hosting activations, and
            leading by example on the road to the Ball.
          </p>
          <form onSubmit={submitAmb} className="space-y-6" data-testid="ambassador-form">
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="amb-name" className={labelCls}>Name</Label>
                <Input id="amb-name" data-testid="ambassador-name-input" value={amb.name} onChange={(e) => setAmb({ ...amb, name: e.target.value })} required className={inputCls} placeholder="Your full name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amb-city" className={labelCls}>City</Label>
                <Input id="amb-city" data-testid="ambassador-city-input" value={amb.city} onChange={(e) => setAmb({ ...amb, city: e.target.value })} className={inputCls} placeholder="Your city" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="amb-email" className={labelCls}>Email</Label>
              <Input id="amb-email" type="email" data-testid="ambassador-email-input" value={amb.email} onChange={(e) => setAmb({ ...amb, email: e.target.value })} required className={inputCls} placeholder="you@example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="amb-why" className={labelCls}>Why you? (optional)</Label>
              <Textarea id="amb-why" data-testid="ambassador-why-input" value={amb.why} onChange={(e) => setAmb({ ...amb, why: e.target.value })} rows={3}
                className={`${inputCls} resize-none`} placeholder="One honest sentence is plenty." />
            </div>
            <button type="submit" disabled={busyA} data-testid="ambassador-submit-button"
              className="w-full border border-[#8A5A0F] py-4 font-mono text-xs uppercase tracking-[0.25em] text-[#8A5A0F] transition-colors duration-300 hover:bg-[#33240E] hover:text-[#F6E7C1] disabled:opacity-50">
              {busyA ? "Joining..." : "Step Up as Ambassador"}
            </button>
          </form>
        </motion.div>
      </div>

      <Dialog open={!!cert} onOpenChange={(open) => !open && setCert(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto border-[#33240E]/25 bg-[#FFF9EA] text-[#33240E] sm:max-w-2xl" data-testid="certificate-dialog">
          {cert && (() => {
            const certUrl = `${API}/pledge/certificate?name=${encodeURIComponent(cert.name)}&n=${cert.n}`;
            const shareText = 'I just took the Kindness Pledge with Be Kind Community Inc — "I will be the reason someone believes in people today." Join the movement:';
            const siteUrl = window.location.origin;
            const shareNative = async () => {
              try {
                const blob = await fetch(certUrl).then((r) => r.blob());
                const file = new File([blob], "kindness-pledge-certificate.png", { type: "image/png" });
                if (navigator.canShare && navigator.canShare({ files: [file] })) {
                  await navigator.share({ files: [file], text: shareText });
                } else if (navigator.share) {
                  await navigator.share({ title: "My Kindness Pledge", text: shareText, url: siteUrl });
                }
              } catch { /* user closed the share sheet */ }
            };
            return (
              <>
                <DialogHeader>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8A5A0F]">Pledge No. {cert.n.toLocaleString()} of 1,000,000</p>
                  <DialogTitle className="font-serif text-3xl font-bold" data-testid="certificate-title">It's official, {cert.name.split(" ")[0]}.</DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    Your certificate is ready. Download it, share it — then prove it on the calendar.
                  </DialogDescription>
                </DialogHeader>
                <img src={certUrl} alt="Your Kindness Pledge certificate" className="w-full border border-[#33240E]/30" data-testid="certificate-preview" />
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <a href={certUrl} download="kindness-pledge-certificate.png" data-testid="certificate-download"
                    className="bg-[#33240E] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#F6E7C1] transition-colors duration-300 hover:bg-[#4A3517]">
                    Download Certificate
                  </a>
                  <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(siteUrl)}`}
                    target="_blank" rel="noreferrer" data-testid="certificate-share-x"
                    className="border border-[#33240E]/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#33240E] transition-colors duration-300 hover:border-[#8A5A0F] hover:text-[#8A5A0F]">
                    Share on X
                  </a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}&quote=${encodeURIComponent(shareText)}`}
                    target="_blank" rel="noreferrer" data-testid="certificate-share-facebook"
                    className="border border-[#33240E]/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#33240E] transition-colors duration-300 hover:border-[#8A5A0F] hover:text-[#8A5A0F]">
                    Share on Facebook
                  </a>
                  <button onClick={() => { navigator.clipboard.writeText(`${shareText} ${siteUrl}`); toast.success("Copied — paste it anywhere."); }}
                    data-testid="certificate-copy-link"
                    className="border border-[#33240E]/25 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#33240E] transition-colors duration-300 hover:border-[#8A5A0F] hover:text-[#8A5A0F]">
                    Copy Link
                  </button>
                  {typeof navigator !== "undefined" && !!navigator.share && (
                    <button onClick={shareNative} data-testid="certificate-share-native"
                      className="border border-[#8A5A0F] px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#8A5A0F] transition-colors duration-300 hover:bg-[#33240E] hover:text-[#F6E7C1]">
                      Share Image
                    </button>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </section>
  );
}
